/**
 * agent-config.ts — Studio Phase 4 AgentConfig.
 *
 * Mirrors the cogo-starter pilot (cogo-web PR #223) pattern:
 *   - Echo adapter only: commercial Studio chat-agent must not call Google
 *   - defaultConfirmationPolicy: read-only auto, write requires confirm
 *
 * Native UI block generation already uses qwen/vllm elsewhere. This file
 * is the agent chat LLM adapter only. There is no documented Studio-agent
 * vllm adapter in this tree, so resolveLlmAdapter fail-closes to echo.
 *
 * This file is intentionally self-contained (no runtime coupling to
 * cogo-starter/) so the two agents can evolve independently.
 */

import {
  requiresConfirmation as runtimeRequiresConfirmation,
  type WebMCPTool,
  type ToolManifestEntry,
} from '@cogo/webmcp-runtime'

// ── Types ────────────────────────────────────────────────────

export interface ProposedToolCall {
  id: string
  tool: ToolManifestEntry
  args: Record<string, unknown>
}

export type ConfirmationPolicyVerdict = 'auto' | 'confirm'

export interface LlmAdapter {
  readonly id: string
  sendMessage(params: {
    userMessage: string
    tools: ToolManifestEntry[]
    systemInstructions?: string
  }): Promise<{ reply?: string; toolCalls: ProposedToolCall[] }>
}

export interface AgentConfig {
  persona: 'studio' | 'dashboard' | 'commerce' | 'ide'
  confirmationPolicy(call: ProposedToolCall): ConfirmationPolicyVerdict
  llmAdapter: LlmAdapter
}

// ── Default confirmation policy ─────────────────────────────

/**
 * Default Studio policy:
 *   1. `metadata.readOnly === true` → auto-approve.
 *   2. Runtime heuristic (destructive name, checkout/payment tags) → confirm.
 *   3. Tool tagged as `write` → confirm.
 *   4. Otherwise → auto.
 *
 * Navigation + context introspection are read-only; project/block/commerce
 * mutations are tagged `write` so they always require user confirmation.
 */
export function defaultConfirmationPolicy(call: ProposedToolCall): ConfirmationPolicyVerdict {
  const { tool } = call

  if (tool.metadata?.readOnly) return 'auto'

  // Adapt ToolManifestEntry to WebMCPTool for the runtime heuristic
  // (it only touches name + metadata; handler is irrelevant here).
  const adapted: WebMCPTool = {
    name: tool.name,
    description: tool.description,
    inputSchema: tool.inputSchema,
    outputSchema: tool.outputSchema,
    metadata: tool.metadata,
    handler: async () => undefined,
  }
  if (runtimeRequiresConfirmation(adapted, call.args)) return 'confirm'

  if (tool.metadata?.tags?.includes('write')) return 'confirm'
  return 'auto'
}

// ── LLM adapters ────────────────────────────────────────────

function parseInlineToolArgs(userMessage: string): Record<string, unknown> {
  const start = userMessage.indexOf('{')
  const end = userMessage.lastIndexOf('}')
  if (start === -1 || end === -1 || end < start) return {}
  try {
    const parsed = JSON.parse(userMessage.slice(start, end + 1))
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function findExplicitTool(userMessage: string, tools: ToolManifestEntry[]): ToolManifestEntry | null {
  return (
    tools.find((tool) => {
      const commandPattern = new RegExp(`(^|\\s)${escapeRegExp(tool.name)}(?=\\s|\\{|$)`, 'i')
      return commandPattern.test(userMessage)
    }) ?? null
  )
}

/**
 * Echo adapter — fail-closed Studio chat-agent LLM.
 * Heuristically matches the user's message against tool names/descriptions
 * and proposes the best-scoring match. Keeps the UI functional without an
 * external cloud LLM.
 */
export function createEchoAdapter(): LlmAdapter {
  return {
    id: 'echo-adapter',
    async sendMessage({ userMessage, tools }) {
      const explicitTool = findExplicitTool(userMessage, tools)
      if (explicitTool) {
        const call: ProposedToolCall = {
          id: `echo-${Date.now()}`,
          tool: explicitTool,
          args: parseInlineToolArgs(userMessage),
        }
        return {
          reply: `Proposing tool "${explicitTool.name}" from the chat command.`,
          toolCalls: [call],
        }
      }

      const lower = userMessage.toLowerCase()
      const keywords = lower.split(/[^a-z0-9]+/).filter((w) => w.length >= 3)

      const scored = tools.map((t) => {
        const haystack = `${t.name} ${t.description}`.toLowerCase()
        let score = 0
        for (const kw of keywords) {
          if (t.name.toLowerCase().includes(kw)) score += 3
          if (haystack.includes(kw)) score += 1
        }
        return { tool: t, score }
      })
      scored.sort((a, b) => b.score - a.score)

      const best = scored[0]
      if (!best || best.score === 0) {
        return {
          reply:
            "Try phrases that match a tool name, e.g. 'navigate to commerce' or 'open project'.",
          toolCalls: [],
        }
      }

      const call: ProposedToolCall = {
        id: `echo-${Date.now()}`,
        tool: best.tool,
        args: {},
      }
      return {
        reply: `Proposing tool "${best.tool.name}" based on keyword match.`,
        toolCalls: [call],
      }
    },
  }
}

/** Build the adapter used by the Studio agent. Always echo — never Google. */
export function resolveLlmAdapter(): LlmAdapter {
  return createEchoAdapter()
}

// ── Exported config instance ────────────────────────────────

export const STUDIO_AGENT_CONFIG: AgentConfig = {
  persona: 'studio',
  confirmationPolicy: defaultConfirmationPolicy,
  llmAdapter: resolveLlmAdapter(),
}
